import {create, StateCreator, StoreApi, UseBoundStore} from "zustand";
import {devtools} from "zustand/middleware";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";

/**
 * State interface for the Auto Zoom-to-Fit store
 *
 * This store manages the continuous zoom-to-fit mode that activates during Autopilot sessions.
 * When enabled, the schematic canvas automatically zooms to fit whenever components or net
 * connections are added.
 */
export interface AutoZoomToFitState {
    /**
     * Whether auto zoom-to-fit mode is currently enabled.
     * This is automatically enabled when an Autopilot session starts and can be
     * toggled off by clicking the zoom-to-fit button.
     */
    isAutoZoomToFitEnabled: boolean;

    /**
     * Enable auto zoom-to-fit mode
     */
    enableAutoZoomToFit(): void;

    /**
     * Disable auto zoom-to-fit mode
     */
    disableAutoZoomToFit(): void;

    /**
     * Toggle auto zoom-to-fit mode
     */
    toggleAutoZoomToFit(): void;
}

/**
 * Store type for the Auto Zoom-to-Fit
 */
export type UseAutoZoomToFitStore = UseBoundStore<StoreApi<AutoZoomToFitState>>;

/**
 * Factory function to create the Auto Zoom-to-Fit store
 */
export function createAutoZoomToFitStore(): UseAutoZoomToFitStore {
    const storeConfig: StateCreator<AutoZoomToFitState> = (set) => ({
        // Initial state - disabled by default
        isAutoZoomToFitEnabled: false,

        enableAutoZoomToFit() {
            set(
                (state): AutoZoomToFitState => ({
                    ...state,
                    isAutoZoomToFitEnabled: true,
                }),
            );
        },

        disableAutoZoomToFit() {
            set(
                (state): AutoZoomToFitState => ({
                    ...state,
                    isAutoZoomToFitEnabled: false,
                }),
            );
        },

        toggleAutoZoomToFit() {
            set(
                (state): AutoZoomToFitState => ({
                    ...state,
                    isAutoZoomToFitEnabled: !state.isAutoZoomToFitEnabled,
                }),
            );
        },
    });

    return create<AutoZoomToFitState>()(
        devtools(storeConfig, {
            enabled: isDevEnv() && !areWeTestingWithJest(),
            name: "AutoZoomToFitStore",
        }),
    );
}
